import { useCallback, useEffect, useState } from "react";

/**
 * Three-state theme: light | dark | system. The bootstrap script in
 * index.html applies the initial class before React mounts (no flash);
 * this hook owns the runtime toggle + persistence.
 *
 * "system" follows `prefers-color-scheme` and re-evaluates when the OS
 * setting flips (operators on macOS auto-dark schedules notice this).
 */
export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "aether:theme";

function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // localStorage may be unavailable (private mode, file://); fall through.
  }
  return "system";
}

function applyClass(theme: Theme) {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const resolved = theme === "system" ? (prefersDark ? "dark" : "light") : theme;
  // v0.4.78: drive both the v0.5 selector and the legacy `.dark` class so
  // tokens.css blocks and any not-yet-migrated `dark:` utilities both fire.
  document.documentElement.setAttribute("data-theme", resolved);
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

export function useTheme(): { theme: Theme; setTheme(t: Theme): void; cycle(): void } {
  const [theme, setThemeState] = useState<Theme>(readStored);

  const setTheme = useCallback((t: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // ignore — class still applies for the session.
    }
    applyClass(t);
    setThemeState(t);
  }, []);

  // Re-apply when OS theme changes and we're following it.
  useEffect(() => {
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyClass("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  // Cycle: system → light → dark → system. Matches the natural mental
  // model of "let me try the other one" without forcing the operator to
  // think about which mode they're in.
  const cycle = useCallback(() => {
    setTheme(theme === "system" ? "light" : theme === "light" ? "dark" : "system");
  }, [theme, setTheme]);

  return { theme, setTheme, cycle };
}
